import {
	queryOptions,
	useMutation,
	useSuspenseQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	getChatSessionsList,
	deleteChatSessionPermanently,
	createChatSession,
	updateChatSessionTitle,
	getChatMessagesBySessionId,
} from "~/lib/server/functions/chat-sessions";
import type { ChatMode } from "~/lib/server/db/schema/chat-schema";

export interface ClientChatSession {
	id: string;
	title: string;
	mode: ChatMode;
	createdAt: string;
	updatedAt: string;
}

export const chatSessionsQueryKeys = {
	all: ["chatSessions"] as const,
	byMode: (mode?: ChatMode) => ["chatSessions", mode] as const,
};

export const chatSessionsListQueryOptions = (mode?: ChatMode) =>
	queryOptions({
		queryKey: chatSessionsQueryKeys.byMode(mode),
		queryFn: async () => {
			const sessionsFromServer = await getChatSessionsList({ data: { mode } });
			return sessionsFromServer.map(
				(session): ClientChatSession => ({
					...session,
					createdAt: session.createdAt?.toISOString() ?? "",
					updatedAt: session.updatedAt?.toISOString() ?? "",
				}),
			);
		},
	});

export function useChatSessionsList(mode?: ChatMode) {
	return useSuspenseQuery(chatSessionsListQueryOptions(mode));
}

interface CreateSessionVariables {
	initialMessageContent: string;
	mode?: ChatMode;
}

interface CreateSessionResult {
	id: string;
	title: string;
	mode: ChatMode;
	createdAt: string;
	updatedAt: string;
}

export function useCreateChatSession() {
	const queryClient = useQueryClient();
	return useMutation<CreateSessionResult, Error, CreateSessionVariables>({
		mutationFn: (variables) => createChatSession({ data: variables }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
		},
	});
}

type DeleteSessionVariables = {
	sessionId: string;
};

export function useDeleteChatSessionPermanently() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (variables: DeleteSessionVariables) =>
			deleteChatSessionPermanently({ data: { id: variables.sessionId } }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
		},
	});
}

type UpdateSessionTitleVariables = {
	id: string;
	title: string;
};

type UpdateSessionTitleResult = {
	id: string;
	title: string;
	createdAt: string;
	updatedAt: string;
};

export function useUpdateChatSessionTitle() {
	const queryClient = useQueryClient();
	return useMutation<
		UpdateSessionTitleResult,
		Error,
		UpdateSessionTitleVariables
	>({
		mutationFn: (variables) => updateChatSessionTitle({ data: variables }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
		},
	});
}

export const chatMessagesQueryKeys = {
	all: ["chatMessages"] as const,
	byChatId: (chatId: string) => ["chatMessages", chatId] as const,
};

export const chatMessagesQueryOptions = (chatId: string) =>
	queryOptions({
		queryKey: chatMessagesQueryKeys.byChatId(chatId),
		queryFn: async () => {
			return getChatMessagesBySessionId({ data: { id: chatId } });
		},
		enabled: chatId !== "",
	});

export function useChatMessages(chatId: string) {
	return useSuspenseQuery(chatMessagesQueryOptions(chatId));
}

